import { createColumnHelper } from '@tanstack/react-table';
import { pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { LocalizedEnum, LocalizedText, LocalizedWallClockDate, lt, WallClockDateService } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { ColumnFilterType, MultiSelectOption, Stack, Tag } from '@optimus-web/core';
import { VoucherOverview, VoucherType } from '@mews-commander/data-access';
import { RouteName, TextKey } from '@mews-commander/enums';
import { RouteLink } from '@mews-commander/ui';

const byName = pipe(
    string.Ord,
    ord.contramap((p: MultiSelectOption) => p.value)
);

const createOptions = () =>
    pipe(
        Object.values(VoucherType).map(
            (value): MultiSelectOption => ({
                value,
                label: <LocalizedEnum prefix="VoucherType" value={value} />,
            })
        ),
        array.sortBy([byName])
    );

const columnHelper = createColumnHelper<VoucherOverview>();

export const columns = [
    columnHelper.accessor('name', {
        header: () => lt('Name'),
        cell: (props) => {
            const value = props.getValue();

            return (
                <RouteLink route={RouteName.VoucherDetail} params={{ id: props.row.original.id }} inherit underline>
                    {value}
                </RouteLink>
            );
        },
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('rates', {
        header: () => lt('AssignedRates'),
        cell: (props) => {
            const value = props.getValue();

            return !value.length ? (
                UnicodeEntity.Dash
            ) : (
                <Stack vertical>
                    {value.map(({ id, name }) => (
                        <RouteLink
                            key={`${props.row.original.id}-${id}`}
                            route={RouteName.RateDetail}
                            params={{ rateId: id }}
                            inherit
                            underline
                        >
                            {name}
                        </RouteLink>
                    ))}
                </Stack>
            );
        },
    }),
    columnHelper.accessor('voucherCodes', {
        header: () => lt('VoucherCodePlural'),
        cell: (props) => {
            const value = props.getValue();

            return !value?.length
                ? UnicodeEntity.Dash
                : value.map(({ value: voucherCodeValue }) => voucherCodeValue).join(', ');
        },
    }),
    columnHelper.accessor('type', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: (props) => {
            const value = props.getValue();

            return (
                <Tag variant={value === VoucherType.Public ? 'primary' : 'basicSubtle'} outline>
                    <LocalizedEnum prefix="VoucherType" value={value} />
                </Tag>
            );
        },
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.MultiSelect,
            getMultiSelectOptions: createOptions,
        },
        enableSorting: true,
    }),
    columnHelper.accessor('companyName', {
        header: () => lt('Company'),
        cell: (props) => {
            const value = props.getValue();

            return value || UnicodeEntity.Dash;
        },
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('occupiableIntervalStart', {
        id: 'occupiableIntervalStart',
        header: () => lt('VoucherStayDateFrom'),
        cell: (props) => {
            const value = props.getValue();

            return value != null ? (
                <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />
            ) : (
                UnicodeEntity.Dash
            );
        },
        enableSorting: true,
    }),
    columnHelper.accessor('occupiableIntervalEnd', {
        id: 'occupiableIntervalEnd',
        header: () => lt('VoucherStayDateTo'),
        cell: (props) => {
            const value = props.getValue();

            return value != null ? (
                <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />
            ) : (
                UnicodeEntity.Dash
            );
        },
        enableSorting: true,
    }),
];
